import { Component, OnInit, OnDestroy } from '@angular/core';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { CommunicationService } from 'src/app/services/communication';
import { ParameterHandlerService, CacheService } from 'projects/common/src/app/services';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { groupBy } from 'projects/common/src/app/services/utils/utilities';
import Highcharts from 'highcharts';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';

@Component({
  selector: 'app-machine-status-by-shift',
  templateUrl: './machine-status-by-shift.component.html',
  styleUrls: ['./machine-status-by-shift.component.scss'],
})
export class MachineStatusByShiftComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private datePipe: DatePresentationPipe,
    private language: LanguageService,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
    };
  }

  /**
   * DrawView
   */
  public drawView(chartOptions: any): void {
    chartOptions.machine = chartOptions;

    this.hasData = !!chartOptions.machine.length;
    const groupByMachine = groupBy(chartOptions.machine, (x) => x.machineName);
    const groupByStatus = groupBy(chartOptions.machine, (x) => x.parentStatusName);
    const machinesName = Object.keys(groupByMachine);
    const series = Object.values(groupByStatus).map((status) => {
      return {
        name: status[0].parentStatusName,
        color: status[0].color,
        data: [],
        machineName: '',
      };
    });

    Object.values(groupByMachine).forEach((machine: any) => {
      machine.forEach((statusLog) => {
        const serie = series.find((serie) => serie.name === statusLog.parentStatusName);
        const startDate = getMoment(statusLog.createdAt.slice(0, -6)).utcOffset(0, true);
        const endDate = getMoment(statusLog.nextCreatedAt.slice(0, -6)).utcOffset(0, true);
        serie.machineName = statusLog.machineName;
        serie.data.push(
          [startDate.valueOf(), machinesName.indexOf(statusLog.machineName), statusLog.machineName],
          [endDate.valueOf(), machinesName.indexOf(statusLog.machineName), statusLog.machineName],
          [endDate.valueOf(), null],
        );
      });
    });

    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const machineTypeName = this.language.message(machineType.name);
    const statusLang = this.language.message('status');
    const date = this.parameterService.parameters.item('date').value;
    const shiftValue = this.parameterService.parameters.item('shift').value;
    const shiftName = shiftValue.name;
    const user = this.parameterService.parameters.item('user-data').value;

    const xAxisText = this.language.message('time');
    const yAxisText = this.language.message('equipments');

    const data = {
      chart: {
        type: 'scatter',
        backgroundColor: '#F8F8F8',
        height: 600,
        style: {
          fontFamily: 'Open Sans',
        },
      },
      title: {
        text: '',
      },
      subtitle: {
        text: `${this.language.message('company')} : ${user.company.name},
                  ${this.language.message('device')}: ${machineTypeName},
                  ${this.language.message('date')}: ${this.datePipe.transform(date)},
                  ${this.language.message('shift_')}: ${shiftName}`,
      },
      tooltip: {
        backgroundColor: '#333333',
        borderColor: '#333333',
        borderRadius: 10,
        headerFormat: '',
        style: {
          color: 'white',
          fontFamily: 'Open Sans',
          fontSize: '10px',
        },
        shared: false,
        useHTML: true,
        pointFormatter(): string {
          // Time format by data
          return `
              <div style="width: 150px">
              ${statusLang} ${this.series.name} <br>
                ${this.series.yAxis.categories[this.y]} <br>
                <div style="margin-top: 2px;">
                  <svg width="20" height="13" style="float: left; margin-right: 5px; border-radius: 1px;">
                    <rect width="20" height="13" style="fill:${this.color};"/>
                  </svg>
                  <span>${Highcharts.dateFormat('%e/%m/%Y %H:%M', this.x)} hrs
                </div>
              </div>
            `;
        },
      },
      legend: {
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        symbolHeight: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        margin: 50,
        reversed: true,
        itemStyle: {
          fontSize: 12,
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#433E3E',
        },
        useHTML: true,
        labelFormatter(): string {
          return `
            <div style=" height: 20px; display: flex;">
              <svg width="36" height="18" style=" float: left;">
                <rect width="30" height="12" x="2" y="2"
                style="fill: ${this.userOptions.color}80; stroke-width: 2px !important;
                stroke: ${this.userOptions.color} !important;"/>
              </svg>

              <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
              color: #433E3E; float: left;">${this.userOptions.name}</span>
            </div>`;
        },
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          lineWidth: 5,
          events: {
            legendItemClick(): boolean {
              return false;
            },
          },
          marker: {
            symbol: 'circle',
          },
        },
      },
      xAxis: {
        title: {
          text: xAxisText.toUpperCase(),
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
        type: 'datetime',
      },
      yAxis: {
        title: {
          text: yAxisText.toUpperCase(),
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
        min: 0,
        max: machinesName.length > 25 ? 25 : machinesName.length - 1,
        scrollbar: {
          enabled: machinesName.length > 25,
          barBackgroundColor: '#707070',
          barBorderRadius: 0,
          barBorderWidth: 0,
          buttonBorderWidth: 0,
          buttonBackgroundColor: 'none',
          trackBackgroundColor: '#F5F5F5',
          trackBorderWidth: 1,
          trackBorderColor: '#CCC',
          buttonArrowColor: 'none',
          buttonArrow: 'none',
          height: 10,
          rifleColor: 'none',
        },
        tickInterval: 1,
        categories: machinesName,
      },
      series,
    };

    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
